import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Plus, Minus, CalendarPlus, Share2, Check, Bell, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

import { BACKEND_URL } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import WaitlistDialog from '../components/WaitlistDialog';
import HostedBySection from '../components/HostedBySection';

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(null); // { message, status }
  const [selectedTickets, setSelectedTickets] = useState({});
  const [waitlistTicket, setWaitlistTicket] = useState(null);
  // Fire view_event only once per event page mount — guards against the
  // React 18 dev-mode double-invoke and prevents resending on retries.
  const viewTrackedRef = useRef(null);
  const [shareCopied, setShareCopied] = useState(false);

  const handleShareEvent = async () => {
    const url = window.location.href;
    // Use Web Share API on mobile when available (native sheet)
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.title || 'VinePass event',
          text: event?.title ? `Check out "${event.title}" on VinePass` : 'Check out this event on VinePass',
          url,
        });
        return;
      } catch (err) {
        // user cancelled — fall through to clipboard
        if (err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      toast.success('Event link copied');
      setTimeout(() => setShareCopied(false), 2200);
    } catch {
      toast.error('Could not copy link');
    }
  };

  useEffect(() => {
    fetchEventDetails();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/events/${eventId}`);
      setEvent(response.data);
      if (viewTrackedRef.current !== eventId) {
        viewTrackedRef.current = eventId;
        trackEvent('view_event', {
          event_id: response.data?.event_id,
          title: response.data?.title,
          category: response.data?.category,
        });
      }
    } catch (error) {
      // 410 Gone = admin-moderated; render a clean "unavailable" page
      // instead of redirecting silently to /events.
      if (error.response?.status === 410) {
        const detail = error.response?.data?.detail || {};
        setUnavailable({
          message: detail.message || 'This event is currently unavailable.',
          status: detail.status,
        });
        setLoading(false);
        return;
      }
      console.error('Failed to fetch event details:', error);
      toast.error('Event not found');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const updateTicketQuantity = (ticketId, change) => {
    setSelectedTickets(prev => ({
      ...prev,
      [ticketId]: Math.max(0, (prev[ticketId] || 0) + change)
    }));
  };

  const handleAddToCart = () => {
    let addedCount = 0;
    let addedQuantity = 0;
    Object.entries(selectedTickets).forEach(([ticketId, quantity]) => {
      if (quantity > 0) {
        const ticketType = event.ticket_types.find(t => t.ticket_id === ticketId);
        if (ticketType) {
          addToCart(event, ticketType, quantity);
          addedCount++;
          addedQuantity += quantity;
        }
      }
    });

    if (addedCount > 0) {
      trackEvent('add_to_cart', {
        event_id: event?.event_id,
        title: event?.title,
        ticket_types_added: addedCount,
        quantity: addedQuantity,
      });
      toast.success('Added to cart');
      setSelectedTickets({});
    } else {
      toast.error('Please select at least one ticket');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#4A5D50]">Loading event...</div>
      </div>
    );
  }

  if (!event && !unavailable) return null;

  if (unavailable) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4" data-testid="event-unavailable">
        <div className="max-w-md text-center bg-white border border-[#E5E9E6] rounded-2xl p-10 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 4h.01M12 4l8 14H4l8-14z" />
            </svg>
          </div>
          <h1 className="font-serif text-2xl text-[#112318] mb-2">Event unavailable</h1>
          <p className="text-[#4A5D50] text-sm leading-relaxed mb-5">{unavailable.message}</p>
          <a href="/events" className="inline-flex items-center px-5 h-10 rounded-full bg-[#1F3D2B] text-white text-sm hover:bg-[#172E20] transition-colors">
            Browse other events
          </a>
        </div>
      </div>
    );
  }

  // Build the best-available "Open in maps" directions URL.
  // Priority: lat/lng → location string. Returns null when nothing usable.
  const directionsUrl = (() => {
    if (event.latitude != null && event.longitude != null) {
      return `https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`;
    }
    if (event.location && event.location.trim()) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(event.location.trim())}`;
    }
    return null;
  })();

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Event Image */}
      <div className="relative h-[50vh] md:h-[60vh]" data-testid="event-detail-hero">
        <img
          src={event.image_url}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Event Details */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg -mt-24 relative z-10">
          <div className="mb-6">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4E7C59]" data-testid="event-category">
              {event.category}
            </span>
            {event.status === 'cancelled' && (
              <div
                className="mt-3 mb-2 inline-flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-800 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider"
                data-testid="event-cancelled-badge"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-600" />
                Event Cancelled
              </div>
            )}
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-bold text-[#112318] mt-2 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="event-title"
            >
              {event.title}
            </h1>
            <div className="flex flex-wrap gap-4 text-[#4A5D50]">
              <div className="flex items-center" data-testid="event-date">
                <Calendar className="w-5 h-5 mr-2" />
                {event.date} at {event.time}
              </div>
              <div className="flex items-center" data-testid="event-location">
                <MapPin className="w-5 h-5 mr-2" />
                {event.location}
              </div>
              <a
                href={`${BACKEND_URL}/api/events/${event.event_id}/ics`}
                className="inline-flex items-center gap-1.5 text-[#1F3D2B] hover:underline"
                data-testid="event-add-to-calendar"
              >
                <CalendarPlus className="w-4 h-4" />
                Add to Calendar
              </a>
              <button
                onClick={handleShareEvent}
                className="inline-flex items-center gap-1.5 text-[#1F3D2B] hover:underline"
                data-testid="share-event-button"
                aria-label="Copy event link"
              >
                {shareCopied ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-700" />
                    <span className="text-emerald-700">Link copied</span>
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4" />
                    Copy Event Link
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Map (read-only) — only when geo set. Get Directions CTA below
              renders regardless of whether coords are on file. */}
          {event.latitude != null && event.longitude != null && (
            <div className="mb-3 rounded-xl overflow-hidden border border-stone-200" data-testid="event-map">
              <iframe
                title="Event location map"
                width="100%"
                height="280"
                frameBorder="0"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${event.longitude - 0.02}%2C${event.latitude - 0.01}%2C${event.longitude + 0.02}%2C${event.latitude + 0.01}&layer=mapnik&marker=${event.latitude}%2C${event.longitude}`}
                style={{ border: 0 }}
              />
              <div className="px-4 py-2 bg-stone-50 border-t border-stone-200 text-xs text-stone-500 flex justify-between items-center">
                <span>Pin: {event.latitude.toFixed(4)}, {event.longitude.toFixed(4)}</span>
                <a
                  href={`https://www.openstreetmap.org/?mlat=${event.latitude}&mlon=${event.longitude}#map=15/${event.latitude}/${event.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1F3D2B] hover:underline"
                >
                  Open in maps &rarr;
                </a>
              </div>
            </div>
          )}

          {/* Get Directions — primary CTA opens the user's default maps app
              on mobile (Apple Maps on iOS, Google Maps on Android) and Google
              Maps directions in a new tab on desktop. */}
          <div className="mb-8" data-testid="event-directions-block">
            {directionsUrl ? (
              <>
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Get directions to ${event.location || event.title}`}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-6 py-3 text-sm font-medium shadow-sm transition-colors"
                  data-testid="get-directions-button"
                >
                  <Navigation className="w-4 h-4" />
                  Get Directions
                </a>
                <p className="text-xs text-[#9AA89F] mt-2">Opens in your maps app.</p>
              </>
            ) : (
              <p
                className="text-xs text-[#4A5D50] italic"
                data-testid="event-directions-unavailable"
              >
                Location details will be provided by the organiser.
              </p>
            )}
          </div>

          <div className="mb-8">
            <h2
              className="text-2xl sm:text-3xl font-semibold text-[#112318] mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              About This Event
            </h2>
            <p className="text-base leading-relaxed text-[#4A5D50]" data-testid="event-description">
              {event.description}
            </p>
          </div>

          {/* Hosted by — public organiser snippet linking to /organiser/:slug */}
          <HostedBySection event={event} />

          {/* Ticket Selection */}
          <div className="border-t border-[#E5E9E6] pt-8">
            <h2
              className="text-2xl sm:text-3xl font-semibold text-[#112318] mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Select Tickets
            </h2>

            {event.ticket_types && event.ticket_types.length > 0 ? (
              <div className="space-y-4" data-testid="ticket-types-list">
                {event.ticket_types
                  // Defensive filter: hide any ticket type rows that are missing
                  // their type_name (legacy/seed data artefacts) so they don't
                  // render as blank placeholder cards on the public event page.
                  .filter((t) => (t.type_name || '').trim().length > 0)
                  .map((ticket) => {
                  const available = ticket.quantity - ticket.sold;
                  const selected = selectedTickets[ticket.ticket_id] || 0;
                  const eventActive = event.status !== 'cancelled';
                  const waitlistEnabled = ticket.waitlist_enabled !== false; // default true

                  return (
                    <div
                      key={ticket.ticket_id}
                      className="border border-[#E5E9E6] rounded-xl p-4 sm:p-6"
                      data-testid={`ticket-type-${ticket.ticket_id}`}
                    >
                      {/* Header — stacks on mobile so the price never crowds the title */}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                        <div className="min-w-0">
                          <h3 className="text-lg font-semibold text-[#112318]" data-testid="ticket-type-name">
                            {ticket.type_name}
                          </h3>
                          {ticket.description && (
                            <p className="text-sm text-[#4A5D50] mt-1">{ticket.description}</p>
                          )}
                        </div>
                        <div className="sm:text-right flex sm:block items-baseline gap-3">
                          <p className="text-2xl font-bold text-[#1F3D2B]" data-testid="ticket-price">
                            R{ticket.price.toFixed(2)}
                          </p>
                          <p className="text-xs text-[#4A5D50]" data-testid="tickets-available">
                            {available > 0 ? `${available} available` : 'Sold Out'}
                          </p>
                        </div>
                      </div>

                      {available > 0 ? (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                          <div className="flex items-center gap-3">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => updateTicketQuantity(ticket.ticket_id, -1)}
                              disabled={selected === 0}
                              data-testid={`decrease-quantity-${ticket.ticket_id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="text-lg font-semibold w-12 text-center" data-testid={`quantity-${ticket.ticket_id}`}>
                              {selected}
                            </span>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => updateTicketQuantity(ticket.ticket_id, 1)}
                              disabled={selected >= available}
                              data-testid={`increase-quantity-${ticket.ticket_id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          {selected > 0 && (
                            <p className="text-lg font-semibold text-[#1F3D2B]">
                              Subtotal: R{(ticket.price * selected).toFixed(2)}
                            </p>
                          )}
                        </div>
                      ) : waitlistEnabled && eventActive ? (
                        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <p className="text-sm text-[#B04D4D]" data-testid="sold-out-message">Sold Out</p>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setWaitlistTicket(ticket)}
                            className="w-full sm:w-auto rounded-full border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white"
                            data-testid={`join-waitlist-${ticket.ticket_id}`}
                          >
                            <Bell className="w-3.5 h-3.5 mr-1.5" />
                            Join waitlist
                          </Button>
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-[#B04D4D]" data-testid="sold-out-message">Sold Out</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-[#4A5D50]" data-testid="no-tickets-message">No tickets available for this event yet.</p>
            )}

            {event.ticket_types && event.ticket_types.length > 0 && (() => {
              const anyAvailable = event.ticket_types.some((t) => (t.quantity - t.sold) > 0);
              const showAddToCart = anyAvailable && event.status !== 'cancelled';
              return (
                <>
                  {event.status === 'cancelled' && (
                    <div
                      className="mt-6 rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-900"
                      data-testid="event-cancelled-notice"
                    >
                      Sales closed — this event has been cancelled.
                    </div>
                  )}
                  <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                    {showAddToCart && (
                      <Button
                        onClick={handleAddToCart}
                        className="w-full sm:flex-1 bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-6 py-5 sm:py-6 font-medium transition-all shadow-md text-base sm:text-lg disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="add-to-cart-button"
                      >
                        Add to Cart
                      </Button>
                    )}
                    <Button
                      onClick={() => navigate('/events')}
                      variant="outline"
                      className="w-full sm:flex-1 rounded-full px-6 py-5 sm:py-6 text-base sm:text-lg"
                      data-testid="back-to-events-button"
                    >
                      Back to Events
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {waitlistTicket && (
        <WaitlistDialog
          open={!!waitlistTicket}
          onOpenChange={(o) => !o && setWaitlistTicket(null)}
          event={event}
          ticket={waitlistTicket}
        />
      )}
    </div>
  );
};

export default EventDetailPage;